import { toast } from 'sonner'
import { Button } from '@teach/ui'
import type { CourseWithoutBlocks } from '@/types/course'
import { useIsAdmin } from '@/features/auth/useIsAdmin'
import { AccessSection } from './AccessSection'
import { isAccessRaise } from './accessLevels'
import { CourseAppLink } from './CourseAppLink'
import { StatusLoadLine } from './StatusLoadLine'
import { UpdateStatusBlock } from './UpdateStatusBlock'
import type { useCourseAccess } from './useCourseAccess'
import { useCourseUpdate } from './useCourseUpdate'
import { useLiveCourseSlug } from './useLiveCourseSlug'
import type { ViolationGo } from './violationPlace'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
  courseAccess: ReturnType<typeof useCourseAccess>
  categoryIds: number[]
  onCategoryIdsChange: (next: number[]) => void
  /** Проверки, которые не делает api (пустое видео, переполнение составного блока):
   *  ещё идут или упали — повышение закрыто. */
  raiseChecking: boolean
  /** Такая проверка нашла замечание (пустое видео, переполнение): повышение не
   *  уходит, а объясняется уведомлением в момент попытки. */
  raiseRefusal?: string | null
  isCourseAuthor: boolean
  /** Переход к нарушению закрывает окно публикации. */
  onNavigate: () => void
  /** Свой переход к нарушению вместо встроенного (настройки курса, см. `ViolationList`). */
  onGo?: (target: ViolationGo) => void
  /** Плашка про экспериментальный доступ правее селекта (страница настроек). */
  noteBeside?: boolean
  /** Закрытый курс без названия или описания: селект погашен с этой подсказкой. */
  unfilledHint?: string
}

export const PublishDialogBody = ({
  course,
  courseAccess,
  categoryIds,
  onCategoryIdsChange,
  raiseChecking,
  raiseRefusal = null,
  isCourseAuthor,
  onNavigate,
  onGo,
  noteBeside = false,
  unfilledHint,
}: Props) => {
  const m = useMessages(messages)
  const { isAdmin } = useIsAdmin()
  // Отправка обновления живёт здесь, а не в секции «Изменения»: пока летит `submit-update`,
  // смена доступа погашена так же, как во время `PATCH /visibility`.
  const update = useCourseUpdate(course.id)
  const liveSlug = useLiveCourseSlug(course)
  const { moderation, access, applyAccess, isBusy, retryLabel } = courseAccess
  // «Не знаем» ≠ «закрыт»: без состояния модерации секций доступа нет вовсе.
  if (moderation === undefined || access === undefined) {
    return (
      <StatusLoadLine
        isError={courseAccess.moderationError}
        onRetry={courseAccess.reloadModeration}
      />
    )
  }
  const pending = moderation.review?.status === 'pending'
  const locked = isBusy || update.isBusy || !isCourseAuthor
  // Отзыв заявки и «Закрыть доступ» — одно и то же понижение; при `pending` оба живые.
  const close = () => void applyAccess('private')

  return (
    <>
      <UpdateStatusBlock
        course={course}
        moderation={moderation}
        update={update}
        isCourseAuthor={isCourseAuthor}
        accessBusy={isBusy}
        accessRetryLabel={retryLabel}
        onNavigate={onNavigate}
        onGo={onGo}
      />
      <AccessSection
        access={access}
        onAccessChange={(next) => {
          if (raiseRefusal && isAccessRaise(access, next)) {
            toast.error(raiseRefusal)
            return
          }
          void applyAccess(next)
        }}
        categoryIds={categoryIds}
        onCategoryIdsChange={onCategoryIdsChange}
        disabled={locked}
        raiseBlocked={raiseChecking || retryLabel !== null || pending}
        platformBlocked={raiseChecking || raiseRefusal !== null}
        selectLocked={pending}
        catalogAllowed={moderation.canPublishToCatalog}
        retryLabel={retryLabel}
        isAdmin={isAdmin}
        noPermission={!isCourseAuthor}
        course={course}
        noteBeside={noteBeside}
        unfilledHint={unfilledHint}
      />
      {access !== 'private' ? <CourseAppLink slug={liveSlug} /> : null}
      {/* Отдельная кнопка — только на проверке живого курса: селект там заперт, и
          отозвать заявку можно лишь закрытием. Иначе закрывают тем же селектом. */}
      {pending && access !== 'private' ? (
        <Button variant="outline" className="w-full" disabled={locked} onClick={close}>
          {m.closeAccess}
        </Button>
      ) : null}
    </>
  )
}
